import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Check, Settings } from 'lucide-react';
import { Button } from './ui/button';

const CookieConsent = () => {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be changed
    analytics: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('motosocial_cookie_consent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        // Invalid JSON, show banner
        setShowBanner(true);
      }
    }
  }, []);

  const acceptAll = () => {
    const newPreferences = { essential: true, analytics: true };
    setPreferences(newPreferences);
    localStorage.setItem('motosocial_cookie_consent', JSON.stringify(newPreferences));
    localStorage.setItem('motosocial_cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  const acceptEssential = () => {
    const newPreferences = { essential: true, analytics: false };
    setPreferences(newPreferences);
    localStorage.setItem('motosocial_cookie_consent', JSON.stringify(newPreferences));
    localStorage.setItem('motosocial_cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  const savePreferences = () => {
    localStorage.setItem('motosocial_cookie_consent', JSON.stringify(preferences));
    localStorage.setItem('motosocial_cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
              {!showSettings ? (
                // Main Banner
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Cookie className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-2">
                        {isFr ? '🍪 Cookies & Confidentialité' : '🍪 Cookies & Privacy'}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {isFr
                          ? "Nous utilisons des cookies pour améliorer votre expérience. Les cookies essentiels sont nécessaires au fonctionnement du site. Les cookies analytiques nous aident à comprendre comment vous utilisez Moto Social."
                          : "We use cookies to enhance your experience. Essential cookies are necessary for site operation. Analytics cookies help us understand how you use Moto Social."}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                          onClick={acceptAll}
                          className="brand-glow"
                          data-testid="accept-all-cookies"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          {isFr ? 'Tout accepter' : 'Accept All'}
                        </Button>
                        <Button
                          onClick={acceptEssential}
                          variant="outline"
                          data-testid="accept-essential-cookies"
                        >
                          {isFr ? 'Essentiels uniquement' : 'Essential Only'}
                        </Button>
                        <Button
                          onClick={() => setShowSettings(true)}
                          variant="ghost"
                          data-testid="cookie-settings"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          {isFr ? 'Paramètres' : 'Settings'}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">
                        {isFr ? 'En savoir plus : ' : 'Learn more: '}
                        <Link to="/privacy" className="text-primary hover:underline">
                          {isFr ? 'Politique de confidentialité' : 'Privacy Policy'}
                        </Link>
                      </p>
                    </div>
                    <button
                      onClick={acceptEssential}
                      className="text-muted-foreground hover:text-foreground p-1"
                      aria-label="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ) : (
                // Settings Panel
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-heading font-bold text-lg uppercase tracking-tight">
                      {isFr ? 'Paramètres des cookies' : 'Cookie Settings'}
                    </h3>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Essential Cookies */}
                    <div className="p-4 rounded-lg bg-secondary/50 border border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            {isFr ? 'Cookies essentiels' : 'Essential Cookies'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {isFr
                              ? 'Nécessaires au fonctionnement du site (authentification, langue)'
                              : 'Required for site operation (authentication, language)'}
                          </p>
                        </div>
                        <div className="px-3 py-1 rounded-full bg-green-500/10 text-green-500 text-xs font-medium">
                          {isFr ? 'Toujours actif' : 'Always active'}
                        </div>
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="p-4 rounded-lg bg-secondary/50 border border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            {isFr ? 'Cookies analytiques' : 'Analytics Cookies'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {isFr
                              ? 'Nous aident à améliorer le site (PostHog)'
                              : 'Help us improve the site (PostHog)'}
                          </p>
                        </div>
                        <button
                          onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            preferences.analytics ? 'bg-primary' : 'bg-muted'
                          }`}
                          data-testid="toggle-analytics"
                        >
                          <span
                            className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                              preferences.analytics ? 'left-7' : 'left-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button onClick={savePreferences} className="flex-1">
                      {isFr ? 'Enregistrer mes préférences' : 'Save my preferences'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
